/**
 * Payment description options — single source of truth.
 *
 * The dropdown shown in PaymentRowEditor / PaymentSummary's Add-Payment
 * dialog is now segment-type aware. Originally the list was flight-centric
 * (baggage, seat_selection…) which made no sense on accommodations or
 * cruises. Users asked for per-type lists with type-appropriate options
 * (e.g. "Resort Fee" on hotels, "Excursions" on cruises, "Fuel" on car
 * rentals). Per the user, existing payments saved with values that no
 * longer appear in the dropdown for their segment type are left untouched
 * — the saved value still renders via `getDescriptionLabel` (which knows
 * about every legacy + per-type value), it just won't appear as an
 * option until re-selected.
 *
 * Each entry: { value, label }. `value` is what's stored in Mongo, `label`
 * is the human-friendly string. Order in the array = order in the dropdown.
 */

// Per-segment-type option lists. Keep `misc` last on every list so users
// have a consistent "if nothing fits" escape hatch at the bottom.
const OPTIONS_BY_TYPE = {
  flight: [
    { value: "fare", label: "Fare" },
    { value: "upgrade", label: "Upgrade" },
    { value: "baggage", label: "Baggage" },
    { value: "seat_selection", label: "Seat Selection" },
    { value: "meal", label: "Meal" },
    { value: "insurance", label: "Insurance" },
    { value: "taxes", label: "Taxes / Fees" },
    { value: "gratuity", label: "Gratuity" },
    { value: "misc", label: "Misc" },
  ],
  accommodation: [
    { value: "room", label: "Room" },
    { value: "meal", label: "Meal" },
    { value: "resort_fee", label: "Resort Fee" },
    { value: "other_fees", label: "Other Fees" },
    { value: "taxes", label: "Taxes" },
    { value: "gratuity", label: "Gratuity" },
    { value: "insurance", label: "Insurance" },
    { value: "upgrade", label: "Upgrade" },
    { value: "misc", label: "Misc" },
  ],
  cruise: [
    { value: "fare", label: "Fare" },
    { value: "upgrade", label: "Cabin Upgrade" },
    { value: "excursions", label: "Excursions" },
    { value: "meal_drinks_package", label: "Meal / Drinks Package" },
    { value: "gratuity", label: "Gratuity" },
    { value: "port_fees", label: "Port Fees" },
    { value: "taxes", label: "Taxes" },
    { value: "insurance", label: "Insurance" },
    { value: "misc", label: "Misc" },
  ],
  train: [
    { value: "fare", label: "Fare" },
    { value: "upgrade", label: "Upgrade" },
    { value: "seat_selection", label: "Seat Selection" },
    { value: "baggage", label: "Baggage" },
    { value: "meal", label: "Meal" },
    { value: "insurance", label: "Insurance" },
    { value: "taxes", label: "Taxes / Fees" },
    { value: "gratuity", label: "Gratuity" },
    { value: "misc", label: "Misc" },
  ],
  bus: [
    { value: "fare", label: "Fare" },
    { value: "upgrade", label: "Upgrade" },
    { value: "seat_selection", label: "Seat Selection" },
    { value: "baggage", label: "Baggage" },
    { value: "meal", label: "Meal" },
    { value: "insurance", label: "Insurance" },
    { value: "taxes", label: "Taxes / Fees" },
    { value: "gratuity", label: "Gratuity" },
    { value: "misc", label: "Misc" },
  ],
  car_rental: [
    { value: "rental", label: "Rental" },
    { value: "insurance", label: "Insurance" },
    { value: "fuel", label: "Fuel" },
    { value: "tolls", label: "Tolls" },
    { value: "drop_off_fee", label: "Drop-off Fee" },
    { value: "gratuity", label: "Gratuity" },
    { value: "taxes", label: "Taxes / Fees" },
    { value: "misc", label: "Misc" },
  ],
  rideshare: [
    { value: "fare", label: "Fare" },
    { value: "tolls", label: "Tolls" },
    { value: "gratuity", label: "Gratuity" },
    { value: "misc", label: "Misc" },
  ],
  activity: [
    { value: "ticket", label: "Ticket / Admission" },
    { value: "guide_tour", label: "Guide / Tour" },
    { value: "equipment_rental", label: "Equipment Rental" },
    { value: "gratuity", label: "Gratuity" },
    { value: "taxes", label: "Taxes / Fees" },
    { value: "insurance", label: "Insurance" },
    { value: "misc", label: "Misc" },
  ],
};

// Build the global label map (every value ever introduced, including
// legacy ones) so `getDescriptionLabel` can render saved values even when
// they no longer appear in the active dropdown.
const LABEL_MAP = (() => {
  const map = {};
  Object.values(OPTIONS_BY_TYPE).forEach((opts) => {
    opts.forEach(({ value, label }) => {
      map[value] = label;
    });
  });
  // Legacy fallbacks not present in any current per-type list.
  map.fare = map.fare || "Fare";
  return map;
})();

/**
 * Return the dropdown options for a given segment type.
 *
 * When `segmentType` is falsy / unknown (e.g. the standalone Add Payment
 * dialog with no segment selected yet), we return the union of every
 * per-type option de-duped by value, in a reasonable order: common fare
 * variants first, then categorized extras, with Gratuity / Taxes / Misc
 * always available. Per user request, gratuity is included on the
 * standalone dropdown.
 */
export const getDescriptionOptions = (segmentType) => {
  if (segmentType && OPTIONS_BY_TYPE[segmentType]) {
    return OPTIONS_BY_TYPE[segmentType];
  }
  // Union of all options across types, de-duped by value, preserving
  // first-seen order. Misc is force-appended at the end.
  const seen = new Set();
  const union = [];
  Object.values(OPTIONS_BY_TYPE).forEach((opts) => {
    opts.forEach((opt) => {
      if (opt.value === "misc") return;
      if (!seen.has(opt.value)) {
        seen.add(opt.value);
        union.push(opt);
      }
    });
  });
  union.push({ value: "misc", label: "Misc" });
  return union;
};

/**
 * Human label for a stored description value (used by PaymentSummary's
 * record list). Falls back to a prettified version of the raw value when
 * the key is unknown (e.g. a really old custom string).
 */
export const getDescriptionLabel = (value) => {
  if (!value) return "Fare";
  if (LABEL_MAP[value]) return LABEL_MAP[value];
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Default description value to use when initializing a new draft payment
 * for a given segment type. Falls back to "fare" for the generic case
 * (which is also the option present on most lists).
 */
export const getDefaultDescription = (segmentType) => {
  const opts = getDescriptionOptions(segmentType);
  // Prefer 'fare' or 'room' or 'rental' or 'ticket' depending on type.
  const preferred = ["fare", "room", "rental", "ticket"];
  for (const p of preferred) {
    if (opts.find((o) => o.value === p)) return p;
  }
  return opts[0]?.value || "fare";
};
